import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { authStorage } from '@/lib/authStorage';

// Empty in dev (Vite proxies /api). Set VITE_API_BASE_URL for builds served from another origin.
axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

const REFRESH_URL = '/api/auth/sign-up/refresh-token/command';
const SIGN_IN_URL = '/api/auth/admin-sign-in/query';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Setup axios interceptor to add accessToken to every request
axios.interceptors.request.use(
  (config) => {
    const accessToken = authStorage.getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function endSession() {
  authStorage.clear();
  window.location.href = '/login';
}

// Shared by all requests that hit 401 at the same time: one refresh call, one new token pair
// (the backend rotates the refresh token, so a second concurrent call would be rejected).
let refreshing: Promise<string> | null = null;

// Plain client without the interceptors: a failed refresh must not trigger another refresh.
const refreshClient = axios.create({ baseURL: axios.defaults.baseURL });

function refreshAccessToken(): Promise<string> {
  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) return Promise.reject(new Error('No refresh token'));

  refreshing ??= refreshClient
    .post<{ response?: { accessToken: string; refreshToken: string } }>(REFRESH_URL, {
      refreshToken,
      deviceInfo: 'admin-web',
    })
    .then((res) => {
      const tokens = res.data.response;
      if (!tokens?.accessToken || !tokens.refreshToken) throw new Error('Malformed refresh response');
      authStorage.setTokens(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

// On 401: try one token refresh and replay the request; if that fails, end the session.
// Only authentication failures count (missing/expired/invalid token). Authorization failures
// (403-style, returned as 401 with exceptionCode "authorizationDenied") must NOT touch the
// session — the user is logged in, just lacks access to that resource.
axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<any>) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    const failure = error.response?.data?.failureMessage;
    const isAuthorizationDenied =
      failure?.exceptionCode === 'authorizationDenied' ||
      failure?.exceptionName === 'AuthorizationDeniedException';
    const isAuthEndpoint = !!originalRequest?.url && originalRequest.url.includes(SIGN_IN_URL);

    if (
      error.response?.status !== 401 ||
      isAuthorizationDenied ||
      isAuthEndpoint ||
      !originalRequest
    ) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      endSession();
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    try {
      const accessToken = await refreshAccessToken();
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return axios(originalRequest);
    } catch {
      endSession();
      return Promise.reject(error);
    }
  }
);

export default axios;
